/**
 * Serwis bufora cenówek - synchronizacja między urządzeniami
 */

import api from './api';

const cenowkiBufferService = {
  /**
   * Pobierz bufor dla lokalizacji
   */
  async getBuffer(locationId) {
    try {
      const response = await api.get('/cenowki/buffer', {
        params: { location_id: locationId }
      });
      return response.data;
    } catch (error) {
      console.error('Błąd pobierania bufora:', error);
      return { success: false, data: [], error: error.message };
    }
  },

  /**
   * Cena efektywna per lokalizacja: specjalna magazynu (warehouse_product_prices)
   * jesli jest, inaczej cena karty. Zwraca null gdy obiekt nie ma informacji
   * o cenie specjalnej (caller nie zna ceny efektywnej).
   */
  getEffectivePrices(source) {
    if (!source) return null;
    const hasSpecial = source.hasSpecialPrice || source.has_special_price;
    const specialBrutto = source.specialPriceBrutto ?? source.special_price_brutto ?? source.aktualna_cena;
    let brutto = null;
    let netto = null;
    if (hasSpecial && specialBrutto != null) {
      brutto = specialBrutto;
      netto = source.specialPriceNetto ?? source.special_price_netto ?? null;
    } else if ('hasSpecialPrice' in source || 'has_special_price' in source) {
      // caller sprawdzil cene specjalna - brak, wiec obowiazuje cena karty
      brutto = source.cena_sprzedazy_brutto ?? source.price ?? null;
      netto = source.cena_sprzedazy_netto ?? null;
    }
    if (brutto == null) return null;
    if (netto == null || netto === '') {
      // netto = brutto/(1+VAT) tylko dla VAT>0; kaucje/VAT<=0: netto == brutto
      const vat = parseFloat(source.stawka_vat);
      const bruttoNum = parseFloat(brutto);
      netto = vat > 0 ? Math.round((bruttoNum / (1 + vat / 100)) * 100) / 100 : bruttoNum;
    }
    return { brutto, netto };
  },

  /**
   * Dodaj produkt do bufora
   */
  async addToBuffer(locationId, product, createdBy = 'user') {
    try {
      // Cena efektywna per lokalizacja (specjalna magazynu -> karta)
      let prices = this.getEffectivePrices(product);
      if (!prices) {
        // caller nie ma ceny efektywnej - pobierz z API produktu per lokalizacja
        try {
          const productResponse = await api.get(`/products/${product.id}`, {
            params: { location_id: locationId }
          });
          const fullProduct = productResponse.data?.data || productResponse.data;
          prices = this.getEffectivePrices(fullProduct);
        } catch (e) {
          // API niedostepne - fallback na dane karty od callera ponizej
        }
      }
      if (!prices) {
        prices = {
          brutto: product.cena_sprzedazy_brutto || product.price,
          netto: product.cena_sprzedazy_netto
        };
      }

      const response = await api.post('/cenowki/buffer', {
        location_id: locationId,
        product_id: product.id,
        product_data: {
          id: product.id,
          nazwa: product.nazwa || product.name,
          nazwa_uproszczona: product.nazwa_uproszczona || product.nazwa || product.name,
          finalName: product.finalName || product.nazwa || product.name,
          ean: product.ean || product.barcode || product.kod_produktu,
          kod_produktu: product.kod_produktu || product.barcode,
          cena_sprzedazy_brutto: prices.brutto,
          cena_sprzedazy_netto: prices.netto,
          stawka_vat: product.stawka_vat || 23,
          gramatura: product.gramatura,
          jednostka_wagi: product.jednostka_wagi,
          // Producent - bez tego podglad bufora (BufferPanel) pokazywal 'Brak',
          // mimo ze podglad/druk (dociagajacy pelny produkt z API) juz go widzial
          producent_id: product.producent_id ?? null,
          manufacturer: product.manufacturer || product.producent || null
        },
        created_by: createdBy
      });
      return response.data;
    } catch (error) {
      console.error('Błąd dodawania do bufora:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Usuń produkt z bufora
   */
  async removeFromBuffer(locationId, productId) {
    try {
      const response = await api.delete(`/cenowki/buffer/product/${productId}`, {
        params: { location_id: locationId }
      });
      return response.data;
    } catch (error) {
      console.error('Błąd usuwania z bufora:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Wyczyść bufor dla lokalizacji
   */
  async clearBuffer(locationId) {
    try {
      const response = await api.delete('/cenowki/buffer/clear', {
        params: { location_id: locationId }
      });
      return response.data;
    } catch (error) {
      console.error('Błąd czyszczenia bufora:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Konwertuj dane z API na format używany przez komponenty
   */
  parseBufferItems(items) {
    return items.map(item => ({
      id: item.product_id,
      bufferId: item.id,
      ...item.product_data,
      created_at: item.created_at
    }));
  }
};

export default cenowkiBufferService;
